import { PathId } from "src/gen/schema/schema_path/path_id";

export interface StreamData {
  id?: PathId;
}

export const mergeStreams = <T extends StreamData>(
  prev: T[],
  curr: T[],
  keyFn: (d: T) => string
): T[] => {
  /**
   * If prev array is large, initialize
   * the Map with it. Map constructor is
   * highly optimized for bulk data.
   * We transform it into [key, value]
   * pairs for the constructor.
   *
   * There is no key check in the Map
   * creation because if the key was missing
   * the items would not already be in the
   * prev array, it was created here in the
   * 1st place, or it is empty.
   */
  const mergeMap = new Map<string, T>(prev.map((d) => [keyFn(d), d]));
  for (const d of curr) {
    // TODO: handle reset
    const key = keyFn(d);
    if (key) {
      /**
       * Tombstone handling: when the server deletes an item it sends the same
       * object back with id.deleted=true instead of removing it from the stream.
       * We remove it from our local map so it disappears from the UI.
       */
      if (d?.id?.deleted) {
        mergeMap.delete(key);
      } else {
        mergeMap.set(key, d);
      }
    }
  }
  /**
   * Use pre-allocation strategy for the output
   * to pre-allocate the exact memory needed.
   */
  const size = mergeMap.size;
  const out = new Array(size);
  let i = 0;
  for (const value of mergeMap.values()) {
    out[i++] = value;
  }
  return out;
};
